import type { ReactElement, ReactNode } from 'react'

import { ClerkProvider } from '@clerk/nextjs'
import { TooltipProvider } from '@radix-ui/react-tooltip'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import { Inter } from 'next/font/google'
import { useRouter } from 'next/router'
import { injectGlobalCss } from 'ui'
import { Provider as UrqlProvider } from 'urql'

import LoadUser from 'domains/user/load-user'
import { urqlClient } from 'graphql/urql'
import { AnalyticsContext } from 'lib/analytics/analytics-context'
import { ThemesProvider } from 'lib/themes/themes-provider'

import Box from 'components/atomic/atoms/box'
import Toasts from 'components/pages/@shared/toasts'

import '../css/global.css'

const Cookies = dynamic(() => import('../components/pages/@shared/cookies'), {
  ssr: false
})

const inter = Inter({
  subsets: ['latin'],
  display: 'swap'
})

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const { asPath } = useRouter()

  // Global Css from stitches
  injectGlobalCss()

  // DOCS has layout for now (Sidebar/Layout persistence)
  const getLayout = Component.getLayout || (page => page)

  return (
    <ClerkProvider clerkJSVariant="headless" {...pageProps}>
      <ThemesProvider>
        <TooltipProvider>
          <AnalyticsContext>
            <UrqlProvider value={urqlClient}>
              <LoadUser>
                <Box relative className={inter.className}>
                  {/* Key => https://github.com/vercel/next.js/issues/9992 */}
                  {getLayout(<Component {...pageProps} key={asPath} />)}
                  <Toasts />
                </Box>
                <Cookies />
              </LoadUser>
            </UrqlProvider>
          </AnalyticsContext>
        </TooltipProvider>
      </ThemesProvider>
    </ClerkProvider>
  )
}

export default MyApp
